"use client";

import { useState } from "react";
import { toast } from "sonner";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

// --- Module-level regexes & maps (Biome: regexes must not live inside loops) ---

const RE_DOI_URL_PREFIX = /^(?:https?:\/\/)?(?:dx\.)?doi\.org\//i;
const RE_DOI_SCHEME_PREFIX = /^doi:\s*/i;
const RE_DOI_VALID = /^10\.\d{4,9}\/\S+$/;
const RE_TRAILING_PUNCT = /[.,;:)\]}]+$/;
const RE_HTML_TAG = /<[^>]+>/g;
const RE_BIBTEX_SPECIAL = /[{}%&]/g;
const RE_HAS_UPPER = /[A-Z]/;
const RE_COMBINING_MARKS = /[\u0300-\u036f]/g;
const RE_NON_ALNUM = /[^a-zA-Z0-9]+/g;
const RE_WHITESPACE = /\s+/;

const BIBTEX_ESCAPES: Record<string, string> = {
  "{": "\\{",
  "}": "\\}",
  "%": "\\%",
  "&": "\\&",
};

/** Crossref work type → closest BibTeX entry type. */
const TYPE_MAP: Record<string, string> = {
  "journal-article": "article",
  book: "book",
  monograph: "book",
  "edited-book": "book",
  "reference-book": "book",
  "book-chapter": "incollection",
  "book-section": "incollection",
  "book-part": "incollection",
  "proceedings-article": "inproceedings",
  report: "techreport",
  dissertation: "phdthesis",
};

const TITLE_STOP_WORDS = new Set([
  "a",
  "an",
  "the",
  "on",
  "of",
  "in",
  "at",
  "to",
  "for",
  "and",
  "or",
  "with",
  "from",
]);

interface CrossrefPerson {
  family?: string;
  given?: string;
  name?: string;
}

interface CrossrefWork {
  type?: string;
  title?: string[];
  "container-title"?: string[];
  author?: CrossrefPerson[];
  editor?: CrossrefPerson[];
  issued?: { "date-parts"?: number[][] };
  volume?: string;
  issue?: string;
  page?: string;
  publisher?: string;
  DOI?: string;
  URL?: string;
}

/** Accept a bare DOI, a doi: prefix, or a full https://doi.org/ URL. */
function normalizeDoi(input: string): string | null {
  const stripped = input
    .trim()
    .replace(RE_DOI_URL_PREFIX, "")
    .replace(RE_DOI_SCHEME_PREFIX, "")
    .replace(RE_TRAILING_PUNCT, "");
  const decoded = safeDecode(stripped);
  return RE_DOI_VALID.test(decoded) ? decoded : null;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Escape BibTeX-special characters ({ } % &) and strip stray HTML tags. */
function escapeBibtex(value: string): string {
  return value
    .replace(RE_HTML_TAG, "")
    .replace(RE_BIBTEX_SPECIAL, (ch) => BIBTEX_ESCAPES[ch] ?? ch);
}

/** Wrap capitalized words in braces so BibTeX styles keep their casing. */
function protectCapitalizedWords(title: string): string {
  return title
    .split(" ")
    .map((word, index) => {
      if (index === 0 || !RE_HAS_UPPER.test(word)) {
        return word;
      }
      return `{${word}}`;
    })
    .join(" ");
}

/** Strip a string to lowercase ASCII letters/digits (for the citation key). */
function keyToken(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(RE_COMBINING_MARKS, "")
    .replace(RE_NON_ALNUM, "")
    .toLowerCase();
}

/** First significant title word for the citation key. */
function firstTitleWord(title: string): string {
  for (const raw of title.trim().split(RE_WHITESPACE)) {
    const cleaned = keyToken(raw);
    if (cleaned.length >= 3 && !TITLE_STOP_WORDS.has(cleaned)) {
      return cleaned;
    }
  }
  return "";
}

function workYear(work: CrossrefWork): string {
  const year = work.issued?.["date-parts"]?.[0]?.[0];
  return year ? String(year) : "";
}

function workTitle(work: CrossrefWork): string {
  return (work.title?.[0] ?? "").replace(RE_HTML_TAG, "").trim();
}

/** authorYEARfirstword citation key, e.g. smith2024quantum. */
function citekey(work: CrossrefWork): string {
  const people = work.author ?? work.editor ?? [];
  const surname = keyToken(people[0]?.family ?? people[0]?.name ?? "unknown");
  const year = workYear(work);
  const word = firstTitleWord(workTitle(work));
  return `${surname || "unknown"}${year}${word}` || "key";
}

/** "Family, Given and Family, Given" BibTeX name list. */
function nameList(people: CrossrefPerson[]): string {
  return people
    .map((p) => {
      if (p.family && p.given) {
        return `${p.family}, ${p.given}`;
      }
      return p.family ?? p.name ?? "";
    })
    .filter(Boolean)
    .map(escapeBibtex)
    .join(" and ");
}

/** Ordered [field, value] pairs for the entry (empty values dropped later). */
function bibtexFields(
  work: CrossrefWork,
  entryType: string,
  protectCaps: boolean
): [string, string][] {
  let title = escapeBibtex(workTitle(work));
  if (protectCaps) {
    title = protectCapitalizedWords(title);
  }
  const container = escapeBibtex(work["container-title"]?.[0] ?? "");
  const fields: [string, string][] = [
    ["author", nameList(work.author ?? [])],
    ["editor", work.author?.length ? "" : nameList(work.editor ?? [])],
    ["title", title],
  ];
  if (entryType === "article") {
    fields.push(["journal", container]);
  } else if (entryType === "incollection" || entryType === "inproceedings") {
    fields.push(["booktitle", container]);
  }
  fields.push(
    ["year", workYear(work)],
    ["volume", work.volume ?? ""],
    ["number", work.issue ?? ""],
    ["pages", work.page ?? ""],
    ["publisher", escapeBibtex(work.publisher ?? "")],
    ["doi", work.DOI ?? ""],
    ["url", work.DOI ? `https://doi.org/${work.DOI}` : (work.URL ?? "")]
  );
  return fields;
}

/** Build the full BibTeX entry text for a Crossref work. */
function buildBibtex(work: CrossrefWork, protectCaps: boolean): string {
  const entryType = TYPE_MAP[work.type ?? ""] ?? "misc";
  const lines = bibtexFields(work, entryType, protectCaps)
    .filter(([, value]) => value.length > 0)
    .map(([field, value]) => `  ${field.padEnd(9)} = {${value}}`);
  return `@${entryType}{${citekey(work)},\n${lines.join(",\n")}\n}`;
}

async function fetchCrossrefWork(
  doi: string,
  strings: typeof DOI_TO_BIBTEX_STRINGS
): Promise<CrossrefWork> {
  const res = await fetch(
    `https://api.crossref.org/works/${encodeURIComponent(doi)}`
  );
  if (res.status === 404) {
    throw new Error(strings.crossrefNotFoundError);
  }
  if (!res.ok) {
    throw new Error(strings.crossrefLookupFailedError);
  }
  const json = (await res.json()) as { message?: CrossrefWork };
  if (!json.message) {
    throw new Error(strings.crossrefUnexpectedResponseError);
  }
  return json.message;
}

/** English strings. A translated content file overrides these through `ui`. */
export const DOI_TO_BIBTEX_STRINGS = {
  doiToConvertAriaLabel: "DOI to convert",
  doiPlaceholder: "10.1038/nphys1170 or https://doi.org/10.1038/nphys1170",
  lookingUp: "Looking up…",
  convert: "Convert",
  protectCaps:
    "Protect capitalized title words with braces ({example}) so BibTeX keeps their casing",
  bibtexEntry: "BibTeX entry",
  copied: "Copied!",
  copyBibtex: "Copy BibTeX",
  notADoiError:
    'That doesn\'t look like a DOI yet. A DOI starts with "10." followed by a registrant code and suffix — e.g. 10.1038/nphys1170 — and the full https://doi.org/... link works too.',
  crossrefNotFoundError:
    "Crossref doesn't know that DOI. Double-check it for typos — and note that some DOIs (e.g. DataCite dataset DOIs) are registered elsewhere and can't be looked up here.",
  crossrefLookupFailedError:
    "The Crossref lookup failed. Please wait a moment and try again.",
  crossrefUnexpectedResponseError:
    "Crossref returned an unexpected response for that DOI.",
  genericLookupError:
    "Something went wrong looking up that DOI. Please try again.",
  copyFailedToast: "Couldn't copy to clipboard. Please copy manually.",
} as const;

export default function DoiToBibtexWidget() {
  const [input, setInput] = useState("");
  const [work, setWork] = useState<CrossrefWork | null>(null);
  const [protectCaps, setProtectCaps] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(DOI_TO_BIBTEX_STRINGS);

  const bibtex = work ? buildBibtex(work, protectCaps) : "";

  async function handleConvert() {
    const doi = normalizeDoi(input);
    if (!doi) {
      setWork(null);
      setError(strings.notADoiError);
      return;
    }
    setLoading(true);
    setError(null);
    setCopied(false);
    try {
      setWork(await fetchCrossrefWork(doi, strings));
    } catch (e) {
      setWork(null);
      setError(e instanceof Error ? e.message : strings.genericLookupError);
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (await writeToClipboard(bibtex)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(strings.copyFailedToast);
    }
  }

  return (
    <>
      {/* Converter */}
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            aria-label={strings.doiToConvertAriaLabel}
            className="flex-1 rounded-lg border border-border bg-card px-4 py-2.5 font-mono text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleConvert();
              }
            }}
            placeholder={strings.doiPlaceholder}
            type="text"
            value={input}
          />
          <button
            className="rounded-lg bg-primary px-5 py-2.5 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
            disabled={loading || input.trim().length === 0}
            onClick={handleConvert}
            type="button"
          >
            {loading ? strings.lookingUp : strings.convert}
          </button>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-muted-foreground text-sm">
          <input
            checked={protectCaps}
            className="accent-primary"
            onChange={(e) => setProtectCaps(e.target.checked)}
            type="checkbox"
          />
          {renderSlots(strings.protectCaps, {
            example: <span className="font-mono">{"{DNA}"}</span>,
          })}
        </label>

        {error ? (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-muted-foreground text-sm leading-relaxed">
              {error}
            </p>
          </div>
        ) : null}

        {bibtex.length > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <p className="font-medium text-foreground text-sm">
                {strings.bibtexEntry}
              </p>
              <button
                className="rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-xs hover:bg-muted"
                onClick={handleCopy}
                type="button"
              >
                {copied ? strings.copied : strings.copyBibtex}
              </button>
            </div>
            <pre className="overflow-x-auto rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed">
              {bibtex}
            </pre>
          </div>
        ) : null}
      </section>
    </>
  );
}
